import { LocationAutocomplete } from './actionTypes';
import { LocationProductType, LocationHotelsSuppliers } from '../../models';
import { LocationUtils } from '../../utils';

const SuggestionsType = Object.freeze({
    OTHERS: 'OTHERS',
});

const initialState = {
    FLIGHT: {
        DEPARTURE: {
            locations: {},
        },
        ARRIVAL: {
            locations: {},
        },
    },
    DP: {
        ARRIVAL: {
            locations: {},
        },
    },
    HOTEL: {
        NON_PRIME: {
            locations: {},
        },
        PRIME: {
            locations: {},
        },
    },
    TRAIN: {
        DEPARTURE: {
            locations: {},
        },
        ARRIVAL: {
            locations: {},
        },
    },
};

const requestLocationAutocomplete = (state, { token, departureOrArrival, locationProductType }) => {
    return {
        ...state,
        [locationProductType]: {
            ...state[locationProductType],
            [departureOrArrival]: {
                locations: {
                    ...state[locationProductType][departureOrArrival].locations,
                    [LocationUtils.getCleanedKey(token)]: {
                        isFetching: true,
                    },
                },
            },
        },
    };
};

const receiveLocationAutocomplete = (
    state,
    { token, locationAutocomplete, departureOrArrival, locationProductType },
) => {
    return {
        ...state,
        [locationProductType]: {
            ...state[locationProductType],
            [departureOrArrival]: {
                locations: {
                    ...state[locationProductType][departureOrArrival].locations,
                    [LocationUtils.getCleanedKey(token)]: {
                        isFetching: false,
                        locationAutocomplete,
                    },
                },
            },
        },
    };
};

const requestHotelLocationAutocomplete = (state, { token, subscription }) => {
    return {
        ...state,
        [LocationProductType.HOTEL]: {
            ...state[LocationProductType.HOTEL],
            [subscription]: {
                locations: {
                    ...state[LocationProductType.HOTEL][subscription].locations,
                    [LocationUtils.getCleanedKey(token)]: {
                        isFetching: true,
                    },
                },
            },
        },
    };
};

const receiveHotelLocationAutocomplete = (
    state,
    {
        token,
        subscription,
        locationAutocomplete,
        locationAccommodationAutocomplete,
        supplier,
        executionTime,
    },
) => {
    const getLocationAutocomplete = () =>
        locationAutocomplete.suggestions.filter(
            (suggestion) => suggestion.type !== SuggestionsType.OTHERS,
        ).map((suggestion) => ({
            countryCode: suggestion.countryCode ?? '',
            ...suggestion,
            geoNodeIdSupplier: supplier,
        }));

    const getHsaLocationAutocomplete = () => {
        const hsaLocationAutocomplete = locationAutocomplete.filter(
            (suggestion) => suggestion.type !== SuggestionsType.OTHERS,
        ).map((suggestion) => ({
            ...suggestion,
            geoNodeIdSupplier: supplier,
        }));
        const hsaLocationAccommodationAutocomplete =
            locationAccommodationAutocomplete?.suggestions?.map((hotel) => ({
                type: hotel.destinationType,
                name: hotel.value,
                countryCode: hotel.countryCode ?? '',
                ...hotel,
                id: hotel.id?.id,
                geoNodeId: hotel.geoNodeId?.id,
                geoNodeIdSupplier: supplier,
            })) || [];
        return hsaLocationAutocomplete.concat(hsaLocationAccommodationAutocomplete);
    };

    return {
        ...state,
        [LocationProductType.HOTEL]: {
            ...state[LocationProductType.HOTEL],
            [subscription]: {
                locations: {
                    ...state[LocationProductType.HOTEL][subscription].locations,
                    [LocationUtils.getCleanedKey(token)]: {
                        executionTime,
                        isFetching: false,
                        locationAutocomplete:
                            supplier === LocationHotelsSuppliers.HSA
                                ? getHsaLocationAutocomplete()
                                : getLocationAutocomplete(),
                    },
                },
            },
        },
    };
};

const splitStringByCommas = (suggestionText) => {
    const [name, region, country, ...rest] = suggestionText.split(',').map((item) => item.trim());
    return {
        name,
        region,
        country: rest.length ? `${country}, ${rest.filter(Boolean).join(', ')}` : country,
    };
};

const receiveHotelLocationAutocompleteV2 = (
    state,
    { token, subscription, locationAutocompleteByRegion, executionTime, supplier },
) => {
    const hsaLocationAutocompleteByRegion =
        locationAutocompleteByRegion.suggestions?.filter(
            (suggestion) => !!suggestion.text
        ).map((suggestion) => ({
            ...splitStringByCommas(suggestion.text),
            type: suggestion.geoType,
            regionType: suggestion.regionType,
            id: suggestion.id.value,
            geoNodeId: null,
            geoNodeIdSupplier: supplier,
            countryCode: suggestion.countryCode ?? '',
        })) || [];

    return {
        ...state,
        [LocationProductType.HOTEL]: {
            ...state[LocationProductType.HOTEL],
            [subscription]: {
                locations: {
                    ...state[LocationProductType.HOTEL][subscription].locations,
                    [LocationUtils.getCleanedKey(token)]: {
                        executionTime,
                        isFetching: false,
                        locationAutocomplete: hsaLocationAutocompleteByRegion,
                    },
                },
            },
        },
    };
};

export default (state = initialState, action) => {
    switch (action.type) {
        case LocationAutocomplete.REQUEST_LOCATION_AUTOCOMPLETE:
            return requestLocationAutocomplete(state, action);
        case LocationAutocomplete.RECEIVE_LOCATION_AUTOCOMPLETE:
            return receiveLocationAutocomplete(state, action);
        case LocationAutocomplete.REQUEST_HOTEL_LOCATION_AUTOCOMPLETE:
            return requestHotelLocationAutocomplete(state, action);
        case LocationAutocomplete.RECEIVE_HOTEL_LOCATION_AUTOCOMPLETE:
            return receiveHotelLocationAutocomplete(state, action);
        case LocationAutocomplete.REQUEST_HOTEL_LOCATION_AUTOCOMPLETE_V2:
            return requestHotelLocationAutocomplete(state, action);
        case LocationAutocomplete.RECEIVE_HOTEL_LOCATION_AUTOCOMPLETE_V2:
            return receiveHotelLocationAutocompleteV2(state, action);
    }
    return state;
};
